<template>
    <div class="p-4">
        <el-card header="Detalhes da loja">
            <el-skeleton :loading="loading" animated :rows="12">
                <template #default>
                    <el-row v-if="!store" justify="center" align="middle">
                        <el-col :sm="12" :lg="7">
                            <el-result
                                icon="error"
                                title="Loja não encontrada"
                                sub-title="A loja que procura nao existe"
                            >
                            </el-result>
                        </el-col>
                    </el-row>

                    <template v-else>
                        <el-descriptions
                            title="Informações da Loja"
                            direction="vertical"
                            :column="3"
                            border
                        >
                            <el-descriptions-item label="Nome">
                                {{ store.name }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Tipo de produto">
                                {{ store.category }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Localização">
                                {{ store.location }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Contacto">
                                {{ store.phone }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Email">
                                {{ store.email }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Descrição">
                                {{ store.description }}
                            </el-descriptions-item>

                            <el-descriptions-item label="Dono da Loja">
                                <span v-if="store.user">
                                    {{ store.user.name }} ({{ store.user.phone }})
                                </span>
                                <span v-else>
                                    —
                                </span>
                            </el-descriptions-item>

                            <el-descriptions-item label="Aprovado por">
                                <span v-if="store.approver">
                                    {{ store.approver.name }}
                                </span>
                                <span v-else>
                                    —
                                </span>
                            </el-descriptions-item>

                            <el-descriptions-item label="Data de aprovação">
                                <span v-if="store.approved_at">
                                    {{ new Date(store.approved_at).toLocaleString() }}
                                </span>
                                <span v-else>
                                    Pendente
                                </span>
                            </el-descriptions-item>
                        </el-descriptions>

                        <el-button plain :loading="loading || updating" :type="store.approved_at ? 'danger':'success'" @click="() => approve(!(store?.approved_at))">
                            <el-icon :size="28">
                                <CircleCloseFilled v-if="store.approved_at" />
                                <Select v-else />
                            </el-icon>
                            <span v-if="store.approved_at" >Bloquear Loja</span>
                            <span v-else>Aprovar Loja</span>
                        </el-button>
                        <hr />

                        <h6></h6>
                        <el-collapse accordion class="my-4">
                                <el-collapse-item name="1" title="Productos da loja">
                                    <ElRow class="pb-5">
                                        <ElInput type="search" name="q" style="flex: 1" v-model="search" class="mx-1" placeholder="Procurar produtos" />
                                        <ElButton :loading="updating || loading" @click="() => getProducts(search)">
                                            <ElIcon>
                                                <Search />
                                            </ElIcon>
                                        </ElButton>
                                    </ElRow>
                                    <el-skeleton :loading="updating" animated :rows="10">
                                        <template #default>
                                            <ProductsList @click-open="openProduct" :items="products.data.map(item => ({
                                                icon: '', 
                                                id: Number(item.id), 
                                                image: '', 
                                                title: item.name,
                                                featured: item.featured,
                                                loading: false,
                                                subtitle: `${item.quantity} unidades | ${item.price.toFixed(2).replace('.', ',')}MT`
                                            }))">
                                            </ProductsList>
                                            <el-pagination 
                                                class="p-2" 
                                                hide-on-single-page 
                                                :default-current-page="products.current_page" 
                                                :page-count="products.last_page" 
                                                @change="(page: number) => getProducts(search, page)"
                                            ></el-pagination>
                                        </template>
                                    </el-skeleton>
                                </el-collapse-item>
                            </el-collapse>
                    </template>
                </template>
            </el-skeleton>
        </el-card>
    </div>
</template>

<script setup lang="ts">
    import ProductsList from '@/components/ProductsList.vue';
    import Api from '@/services/api';
    import { Product } from '@/services/types';
    import { Paginated } from '@/services/types/paginated';
    import { Store } from '@/services/types/store';
    import { defaultPaginated } from '@/utilities/helpers';
    import { ElIcon, ElMessage, ElMessageBox, ElPagination, ElSkeleton } from 'element-plus';
import { it } from 'node:test';
    import { onBeforeMount, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    const route = useRoute()
    const router = useRouter()
    const {id} = route.params
    const store = ref<Store>()
    const loading = ref<boolean>(true)
    const search = ref<string>('')
    const updating = ref<boolean>(false)
    const products = ref<Paginated<Product>>(defaultPaginated)

    const getStore = async () => {
        updating.value = true
        const res = await Api.get('admin/stores/' + id)
        if (res.status == 200) {
            const data = res.data.store
            store.value = data
        } else if (res.status != 404) {
            ElMessageBox.alert('Ocorreu um erro ' + res.data.message, {type: "error"}).then(() => router.push('/products')).catch(() => router.push('/products'))
        }
        updating.value = false
    }

    const getProducts = async (q = '', page = 1) => {
        console.log("Okay")
        updating.value = true
        const res = await Api.get(`admin/stores/products/${id}?q=${q}&page=${page}`)
        if (res.status == 200) {
            products.value = res.data.products
            console.log(res.data.products)
        } else {
            ElMessageBox.alert('Ocorreu um erro ao buscar os dados')
        }

        updating.value = false
    }

    const approve = async (up = true) => {
        updating.value = true
        const res = await (up ? Api.post(`admin/stores/${id}`, {}):Api.delete(`admin/stores/${id}`))
        if (res.status != 200) {
            ElMessage.error({message: 'Ocorreu um erro ' + res.data.message ? res.data.message:''})
        } else {
            ElMessage.success({message: 'Actualizado com sucesso'})
            if (store.value) {
                store.value.approved_at = res.data.store.approved_at
            }
        }
        updating.value = false
    }

    const openProduct = (id: number | string) => {
        router.push('/products/' + id)
    }

    onBeforeMount(async () => {
        loading.value = true
        await getProducts()
        await getStore()

        loading.value = false
    })
</script>